import {
  initializeAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  confirmPasswordReset,
  updateEmail,
  sendEmailVerification,
  updateProfile,
  browserLocalPersistence,
} from 'firebase/auth';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import isStrongPassword from 'validator/lib/isStrongPassword';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { doc, getDoc , setDoc, } from 'firebase/firestore';

import firestore, {Collection, firebaseApp} from './DatabaseService';
import Constant from 'utils/Constant';
import User, { IAuthUser, IProfile, UserValidationErrors } from './User';

export const auth = initializeAuth(firebaseApp, {
  persistence: browserLocalPersistence,
});

class AuthService {

  private current: {
    user: User | null;
    profile: IProfile | null;
  } = {
    user: null,
    profile: null,
  };

  /**
   * Make sure to dispatch `setProfile` after successful registration to init state-level profile.
   * @param rawUser The new user details to create.
   * @returns User if registration is successful.
   */
  public async register(rawUser: IAuthUser): Promise<User> {
    const {user} = await createUserWithEmailAndPassword(
      auth,
      rawUser.email!,
      rawUser.password!,
    );
    updateProfile(user, {
        displayName: `${rawUser.firstName} ${rawUser.lastName}`,
      })
      .finally(() => {
        // Send after updating user's name so that we have the name in the email.
        this.sendVerificationEmail();
      });
    this.current.user = new User(user);
    return this.current.user;
  }

  /**
   * Make sure to dispatch `setProfile` after successful login to init state-level profile.
   * @param loginUser The user to login.
   * @returns User if login is successful.
   */
  public async login(
    loginUser: Pick<IAuthUser, 'email' | 'password'>,
  ): Promise<User> {
    if (!loginUser.email?.trim().length) {
      throw new Error('Email is required.');
    }
    if (!loginUser.password?.trim().length) {
      throw new Error('Password is required.');
    }
    const {user} = await signInWithEmailAndPassword(
      auth,
      loginUser.email,
      loginUser.password,
    );
    this.current.user = new User(user);
    return this.current.user;
  }

  public async setup() {
    if (auth.currentUser !== null) {
      this.current.user = new User(auth.currentUser);
      await this.refreshProfile();
    }
  }

  public async loadProfile(userId: string) {
    const profile = await getDoc(
      doc(firestore, `${Collection.User}/${userId}`),
    );
    return profile.data() as IProfile;
  }

  public get profile() {
    return this.current.profile;
  }

  public get user() {
    return this.current.user;
  }

  public async updateUser(authUser: User | null) {
    this.current.user = authUser;
    await this.refreshProfile();
  }

  public async refreshProfile() {
    if (!this.user) {
      return;
    }
    this.current.profile = await this.loadProfile(this.user.uid);
  }

  public async logout() {
    this.current = {
      user: null,
      profile: null,
    };
    await auth.signOut();
  }

  public async sendResetEmail(user: IAuthUser) {
    const email = user.email?.trim();
    if (!email || !isEmail(email ?? '')) {
      throw new Error('A valid email is required.');
    }
    await sendPasswordResetEmail(auth, email, {
      url: Constant.resetPasswordUrl + '?email=' + email,
      handleCodeInApp: true,
      android: {
        packageName: 'com.halth.Halth',
        installApp: true,
        minimumVersion: '0.0.4',
      },
      iOS: {
        bundleId: 'com.halth.Halth',
      },
    });
  }

  public async sendVerificationEmail() {
    if (auth.currentUser === null) {
      throw new Error('No user is logged in.');
    }
    if (auth.currentUser?.emailVerified) {
      return false;
    }
    await sendEmailVerification(auth.currentUser);
    return true;
  }

  public async refreshUser() {
    await auth.currentUser?.reload();
  }

  public async checkUserExists(email: string) {
    const user = await fetchSignInMethodsForEmail(auth, email);
    return user.length > 0;
  }

  public validateRegistration(current: IAuthUser): UserValidationErrors {
    const errors = {} as UserValidationErrors;
    if (isEmpty(current.firstName ?? '')) {
      errors.firstName = {
        status: 'error',
        description: 'Please, enter your first name.',
      };
    }

    if (isEmpty(current.lastName ?? '')) {
      errors.lastName = {
        status: 'error',
        description: 'Please, enter your last name.',
      };
    }

    if (!isEmail(current.email ?? '')) {
      errors.email = {
        status: 'error',
        description: 'Please, enter your valid email address.',
      };
    }

    if (current.mobileNumber && !isMobilePhone(current.mobileNumber)) {
      errors.mobileNumber = {
        status: 'error',
        description: 'Please, enter your valid mobile number.',
      };
    }

    if ( 
      !isStrongPassword(current.password ?? '', {
        minSymbols: 0,
        minLength: 8,
        minUppercase: 1,
        minNumbers: 1,
      })
    ) {
      errors.password = {
        status: 'error',
        description:
          'Password must have at least 8 characters with 1 uppercase letter and 1 number.',
      };
    }

    if (
      !current.password ||
      current.password !== current.confirmPassword
    ) {
      errors.confirmPassword = !current.password && !current.confirmPassword ? errors.password : {
        status: 'error',
        description: 'Passwords do not match.',
      };
    }

    return errors;
  }

  public async resetPassword(code: string, password: string) {
    try {
      await confirmPasswordReset(auth, code, password);
      return true;
    } catch {
      return false;
    }
  }

  public async updateUserEmail({email}: {email: string}) {
    if (!auth.currentUser) {
      return;
    }
    return updateEmail(auth.currentUser, email);
  }

  public async createProfile(profile: Partial<IProfile>) {
    if (!this.user?.uid) {
      return;
    }
    await setDoc(doc(firestore, `${Collection.User}/${this.user.uid}`), profile);
  }
}

export default AuthService;

